import React, { FC, useCallback, useEffect, useState } from 'react';
import './floor-map.scss';
import { useIonToast, useIonViewDidEnter, useIonViewWillEnter } from '@ionic/react';
import mapboxgl from 'mapbox-gl';
import centroid from '@turf/centroid';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import transformTranslate from '@turf/transform-translate';
import { Feature, Point, Properties, multiLineString, point } from '@turf/helpers';
import bbox from '@turf/bbox';
import getHeatBins from '../../data/heatBins';
import 'mapbox-gl/dist/mapbox-gl.css';
import { titleCase } from '../../common/include';
import HeatLegend from '../HeatLegend';
import { config } from '../../../../config/config';
import { DirectionType } from '../HeatLegend/HeatLegend';
import { FloorMapProps, GeoJson, GeoSlot, IFeatures, Slot } from './IFloormap';
import { InsertGameNameToSlot } from './FloorMapFunctions';
import useMapData, { S3_BASE } from 'components/FloorMapPackage/data/geoJson';
import { useDispatch, useSelector } from 'react-redux';
import { ICasinoSearchData, ISlotState, IState } from 'appRedux/createStore';
import { CasinoInsightsKey, ICasinoReport } from 'appRedux/models/casinoModels';
import {
  floormapTopHeatScale,
  getTopAndBottom,
  getOrderAndHeatKey,
  isJackpotInsight,
  OrderByEnum,
  slotHotnessScale,
  sortByInsightOrder,
  getCleanGameName,
  MapTypeEnum,
} from 'common/common';
import { FloormapReduxCommandCreator } from 'appRedux/actions/floormapCommandCreator';
import { selectHeatFilterAndBearing } from 'appRedux/selectors';
import {
  egmDetailId,
  insightMapLabels,
  isInsightCardRow,
  matchesEgmDetailId,
  rowMatchesSlotFilter,
  withReturnFraction,
} from 'services/insightCasino';
import { TOP_SLOTS_LENGTH } from 'common/include';
import MapControls from './MapControls';
import {
  detailFocusCameraPadding,
  measureDetailFocusTopPadding,
} from './detailFocusPadding';
import { setupCasinoLayers } from './helpers/mapboxCasinoLayers';
import {
  setupAddWallLayer,
  setupBankIdLayers,
  setupBorderLayer,
  setupHeatLayer,
  setupSlotTextLayer,
} from './helpers/mapLayerHelpers';
import { addMapSources } from './helpers/addMapSources';
import { mapboxHelper } from './helpers/mapboxHelper';
import { setInsightFeatureIndex, setInsightRows } from 'services/insightMapJoin';
import {
  shouldAutoFitMapCamera,
  shouldPanToTopPinOnDataReload,
  shouldRecenterEmptyMap,
  resolveTopPinPanZoom,
  resolveFirstLoadZoomAfterTopPin,
  TOP_PIN_PAN_DURATION_MS,
  FIRST_LOAD_EXTRA_ZOOM_DURATION_MS,
} from './mapCameraPolicy';
import {
  bankLabelTextFromProperties,
  existingBankLabelLayerIds,
} from 'lib/bankLabelMapClick';

const slotLocationIdLookup = new Map();
mapboxgl.accessToken = config.mapbox.key;

// Recenter-to-default icon: a location pin over a bullseye target, drawn white via
// currentColor on a transparent background (matches the supplied reference art).
const RecenterIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 64 72"
    fill="none"
    className="icon recenter-icon"
  >
    {/* Concentric target rings: outer at 30% opacity, inner at 70% opacity */}
    <circle cx="32" cy="42" r="25" stroke="currentColor" strokeWidth="4.5" strokeOpacity="0.3" />
    <circle cx="32" cy="42" r="13" stroke="currentColor" strokeWidth="4.5" strokeOpacity="0.7" />
    {/* Dot at the pin's tip (10% transparent → 90% opacity), drawn first so it
        sits behind the pin */}
    <circle cx="32" cy="44" r="3" fill="currentColor" fillOpacity="0.9" />
    {/* Location pin filled with the map-pin gold, with a transparent hole via the
        evenodd fill rule */}
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      fill="#AE9142"
      d="M32 4c-7.7 0-14 6.3-14 14 0 9.6 14 26 14 26s14-16.4 14-26c0-7.7-6.3-14-14-14zm0 9a5 5 0 1 0 0 10 5 5 0 0 0 0-10z"
    />
  </svg>
);

const FloorMap: FC<FloorMapProps> = ({
  mapId,
  slotData,
  slotDataIsLoading,
  individualSlotNumber,
  selectedInsight,
  isShowData,
  locationLatLon,
  selectedSlots,
  showAllMarkers = false,
  cloakedSlotNumbers,
  onClickSlot,
  onDoubleClickSlot,
  starredSlotNumbers = [],
  mapFocusSlot,
  onClickBankLabel,
  onMapMove,
  onClickHeatFilter,
  onSelectHeatLevels,
  coldFocus,
  onFloorStats,
  shouldHideSlotMark,
  previousSelectedSlotNumber,
  geoJsonData,
  getGeoJsonLoading,
  slotMetaData,
  mapBottomPadding = 0,
  drawerDragging = false,
  hideEgmOverlays = false,
}) => {
  const DETAIL_FOCUS_ZOOM = 21; // zoom level when centered on a single machine
  const CHALLENGE_PIN_FOCUS_ZOOM = 16; // moderate zoom for challenge Explore: pin 1 centered, nearby machines still visible
  // Star colors for the focused EGM on the detail map: gold when that EGM is selected
  // in the list, white when it is not (matches the gold-star selection indicator).
  const DETAIL_STAR_GOLD = '#FFC83D';
  const DETAIL_STAR_WHITE = '#fff';

  const [mapMarkers, setMapMarkers] = useState<any[]>([]);
  const [presentMapError] = useIonToast();
  const [heatBins, setHeatBins] = useState<any>();
  // Bumped when the insight feature index is rebuilt (services/insightMapJoin).
  const [insightIndexVersion, setInsightIndexVersion] = useState(0);
  const [markerSlots, setMarkerSlots] = useState<any>([]);
  const [markerColdestSlots, setMarkerColdestSlots] = useState<any>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const dispatch = useDispatch();
  const floormapCommand = FloormapReduxCommandCreator(dispatch);
  const isTopTenSlot = useSelector<IState>(
    (state) => state.app.persistedState.isSlotOrderAscending
  ) as boolean;
  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;
  // GetSearchData machines catalog — display ids (slotNumber) resolve from here.
  // Subscribe so map labels/popups re-run insertGameNames when the catalog arrives
  // on cold start (module-level catalog alone does not trigger React).
  const casinoSearchData = useSelector<IState>(
    (state) => state.app.persistedState.casinoSearchData
  ) as ICasinoSearchData | undefined;
  const isLandscapeOnly = useSelector<IState>(
    (state) => state.app.persistedState.orientationState?.isLandscape
  ) as boolean;
  const mapContainer = React.useRef<any>(null);
  // Once the floor camera has been auto-fitted for this casino, timeframe/insight
  // (and other EGM) reloads must preserve zoom/center/bearing. Reset on casino change.
  const initialCameraFitDoneRef = React.useRef(false);
  // Distinguishes a single tap (focus list) from a double tap (open detail) on a pin.
  const slotClickTimer = React.useRef<any>(null);
  // Current pin click listener, kept so we can off-then-on and never accumulate
  // duplicates when updateMapData re-runs.
  const slotClickHandlerRef = React.useRef<any>(null);
  // Map-level bank-label click (ref + off before on) so refreshes do not stack listeners.
  const bankLabelClickHandlerRef = React.useRef<
    ((evt: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void) | null
  >(null);
  const onClickBankLabelRef = React.useRef(onClickBankLabel);
  onClickBankLabelRef.current = onClickBankLabel;
  // Gold-star overlay markers for EGMs selected in the list; kept separate from the
  // numbered pins so toggling selection never re-runs the pin draw / camera fit.
  const starMarkersRef = React.useRef<mapboxgl.Marker[]>([]);
  // The single star drawn on the focused EGM in the detail (individual) map. Kept in
  // its own ref so its color can be flipped (gold when selected, white when not)
  // in place when the selection toggles, without re-flying the camera.
  const focusedStarMarkerRef = React.useRef<mapboxgl.Marker | null>(null);
  // The pin click listener is registered once inside updateMapData and only re-registered
  // when the map/heat data changes — NOT when onClickSlot/onDoubleClickSlot change. Those
  // callbacks close over parent state (e.g. the active EGM in the detail view), so calling
  // the captured prop directly would use a stale closure once the parent re-renders (e.g.
  // after navigating to a different EGM's detail). Keep the latest callbacks in refs and
  // invoke them through the ref so the handler always runs the current logic without having
  // to re-run updateMapData (which clears heat values and flashes the map).
  const onClickSlotRef = React.useRef(onClickSlot);
  const onDoubleClickSlotRef = React.useRef(onDoubleClickSlot);
  onClickSlotRef.current = onClickSlot;
  onDoubleClickSlotRef.current = onDoubleClickSlot;
  const [map, setMap] = useState<any>(undefined);
  const [slotMapJsonWithIds, setSlotMapJsonWithIds] = useState<GeoJson | null | undefined>(undefined);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const appUserPk = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.appUserPk
  ) as string | number | undefined;
  const { wallMapData, bankIdMapData, labelMapData, slotsMapData, ownBankIdsLoaded } = useMapData(
    casinoId,
    appUserPk
  );
  const [wallMapJson, setWallMapJson] = useState<GeoJson>();
  const [bankIdMapJson, setBankIdMapJson] = useState<GeoJson>();
  const [labelMapJson, setLabelMapJson] = useState<GeoJson>();

  const [mapBounds, setMapBounds] = useState<number[] | undefined>(undefined);
  const [mapCenter, setMapCenter] = useState<Feature<Point, Properties> | undefined>(undefined);
  const [maxBounds, setMaxBounds] = useState<mapboxgl.LngLatBoundsLike | undefined>(undefined);
  const [displayLegend, setDisplayLegend] = useState(false);
  const tooltip = new mapboxgl.Popup({
    closeOnClick: true,
  });
  const [loadError, setLoadError] = useState(false);
  // True while the map camera is in motion (pan/zoom/rotate or a programmatic
  // flyTo). The recenter icon is hidden while moving and fades back in on settle.
  const [mapMoving, setMapMoving] = useState(false);
  const { heatFilter, bearing, pinSelected, pinLatitudeLongitude, pinZoom } = useSelector(
    selectHeatFilterAndBearing
  );
  const challengeEGMPinConfig = useSelector<IState, Record<string, { pinDisplayType: string; pinDisplayCode: string }> | null>(
    (s) => s.app.persistedState.slotState?.challengeEGMPinConfig ?? null
  );
  const challengePinDetails = useSelector<IState, ISlotState['challengePinDetails']>(
    (s) => s.app.persistedState.slotState?.challengePinDetails ?? null
  );
  let individualSlotLngLat: any;

  useEffect(() => {
    wallMapData && setWallMapJson(wallMapData as GeoJson);
    bankIdMapData && setBankIdMapJson(bankIdMapData as GeoJson);
    labelMapData && setLabelMapJson(labelMapData as GeoJson);
    slotsMapData && setSlotMapJsonWithIds(slotsMapData as GeoJson);
    

    if (wallMapData) {
      const { maxBounds, mapCenter } = mapboxHelper.getMapBoundsAndCenter(wallMapData as GeoJson);
      setMapCenter(mapCenter);
      setMaxBounds(maxBounds);
    }
  }, [wallMapData, bankIdMapData, labelMapData, slotsMapData]);

  // Casino switch: allow one fresh auto-fit for the new floor geometry.
  useEffect(() => {
    initialCameraFitDoneRef.current = false;
  }, [casinoId]);

  const getJsonData = useCallback(() => {
    if (map) {
      ['wall', 'label', 'bankId'].forEach((source) => {
        if (map.getSource(`${source}Source`)) {
          map.getSource(`${source}Source`).setData({
            type: 'FeatureCollection',
            features: eval(`${source}MapJson`).features,
          });
        }
      });
    }
    if (wallMapJson) {
      if (map) {
        const { maxBounds, mapCenter } = mapboxHelper.getMapBoundsAndCenter(wallMapJson);
        const newCenter = [mapCenter.geometry.coordinates[1], mapCenter.geometry.coordinates[1]];
        setMapBounds(newCenter);
        map?.setCenter(newCenter);
        map?.setMaxBounds(maxBounds);
      }
      flyToMapCenter(mapCenter?.geometry?.coordinates, 14);
      initialCameraFitDoneRef.current = true;
    }
  }, [casinoId]);

  // EGM Details map: pan (and zoom if needed) so the selected pin is centred in
  // the clear band below the pills and above the 75% drawer — on open and when
  // the EGM changes. flyToMapCenter measures the pills overlay for top padding
  // and uses detailFocusCameraPadding so the first fly is correct even before
  // mapBottomPadding catches up (do not depend on padding here — that would
  // re-pan on every drawer drag).
  useEffect(() => {
    if (!map || !individualSlotNumber || !slotData) return;
    if (!shouldHideSlotMark && individualSlotNumber && !previousSelectedSlotNumber && slotData) {
      clearMapMarkers();
      handleIndividualSlot(slotData, individualSlotNumber, true);
    }
  }, [individualSlotNumber]);

  useEffect(() => {
    getJsonData();
  }, [getJsonData]);

  const processData = (
    slotData: ICasinoReport[],
    selectedInsight: CasinoInsightsKey
  ): ICasinoReport[] => {
    return slotData.map((slot) => {
      let updatedSlot = { ...slot };

      // Insight rows are their own play data (placement is resolved once per
      // slotData in setInsightRows); legacy rows keep the area/section/location find.
      const playDataSlot = isInsightCardRow(updatedSlot)
        ? slot
        : slotData.find(
        (playSlot) =>
          updatedSlot.area === playSlot.area &&
          updatedSlot.section === playSlot.section &&
          updatedSlot.location === playSlot.location
      );

      if (playDataSlot) {
        const insightValue = playDataSlot[selectedInsight];
        if (insightValue !== undefined) {
          updatedSlot[selectedInsight] = insightValue;
        } else {
          updatedSlot[selectedInsight] = 0;
        }
      } else {
        updatedSlot[selectedInsight] = 0;
      }

      return updatedSlot;
    });
  };

  // Effect A: recompute heat bins whenever slot data or insight changes.
  // Does NOT depend on isTopTenSlot so a sort toggle never triggers a heatBins update
  // (which would re-run updateMapData → clearHeatValues → blank map flash).
  useEffect(() => {
    if (!slotDataIsLoading && slotData !== undefined) {
      // Feature -> row map for insight rows, once per slotData / floor plan.
      setInsightRows(slotData);
      const processedData = processData(slotData, selectedInsight);
      setHeatBins(getHeatBins(processedData, selectedInsight));
    }
  }, [slotData, slotDataIsLoading, selectedInsight, insightIndexVersion]);

  // Effect B: recompute marker slot lists. Depends on isTopTenSlot so the correct
  // set of top/bottom markers is ready before the markers-only isTopTenSlot effect fires.
  useEffect(() => {
    if (!slotDataIsLoading && slotData !== undefined) {
      // Cloaked EGMs are dropped here, not in Effect A: they must keep feeding the
      // heat bins (so cloaking one doesn't re-shade the floor) while staying out of
      // every pin set — the drawer list hides them, and a pinned EGM with no list row
      // would take a pin number and push every pin below it out of step with its row.
      const processedData = processData(slotData, selectedInsight).filter((slot) => {
        const id = egmDetailId(slot);
        return id === null || !cloakedSlotNumbers?.has(String(id));
      });
      setMarkerSlots([]);
      setMarkerColdestSlots([]);

      if (showAllMarkers) {
        // Pin the full relevant set with no top/bottom cap and fit-all framing.
        // Game Name/Theme filter: the pin set is the matching selectedSlots.
        // Favorites/Playlist: selectedSlots is empty but slotData is already filtered
        // upstream, so the whole processedData is the pin set.
        const selectedSlotNumbers = (selectedSlots ?? []) as Array<number | string>;
        // Prefer mnum (matchesEgmDetailId); also accept floorLocation/slot_number so
        // Search chips that historically stored placement codes still place pins.
        const findSelectedSlot = (selectedSlotNumber: number | string) =>
          processedData.find(
            (slot) =>
              matchesEgmDetailId(slot, selectedSlotNumber) ||
              rowMatchesSlotFilter(slot, new Set([String(selectedSlotNumber)]))
          );
        const baseSlots: ICasinoReport[] =
          selectedSlotNumbers.length > 0
            ? (selectedSlotNumbers
              .map((selectedSlotNumber) => findSelectedSlot(selectedSlotNumber))
              .filter(Boolean) as ICasinoReport[])
            : processedData;
        const orderedAll = sortByInsightOrder(
          baseSlots,
          selectedInsight,
          isTopTenSlot ? OrderByEnum.Desc : OrderByEnum.Asc
        );
        setMarkerSlots(
          orderedAll.map((slot, index) => ({
            ...slot,
            ...getOrderAndHeatKey(index, OrderByEnum.Desc, selectedInsight),
          }))
        );
      } else if (selectedSlots && selectedSlots?.length > 0) {
        const selectedMarkerSlots = selectedSlots
          .map((selectedSlotNumber: number) => {
            const foundSlot = processedData.find(
              (slot) =>
                matchesEgmDetailId(slot, selectedSlotNumber) ||
                rowMatchesSlotFilter(slot, new Set([String(selectedSlotNumber)]))
            );
            if (foundSlot) {
              return { ...foundSlot, ...getOrderAndHeatKey(0, OrderByEnum.Asc, selectedInsight) };
            }
            // Challenge mode: an assigned EGM with no play data in the active
            // insight/timeframe is absent from processedData (which is derived from the
            // play-data report), so it would otherwise never get a pin. Locate it from
            // slotMetaData — every machine's area/section/location, independent of play
            // data — and build a stub record so createMarker can still place and theme
            // it. This keeps the themed challenge pin on EVERY assigned EGM regardless
            // of recent play. Non-challenge selections (Favorites/Playlist/Game filter)
            // keep their play-data-only behaviour.
            if (challengeEGMPinConfig) {
              const meta = slotMetaData?.DataSet?.find(
                (m) => String(m.slot_number) === String(selectedSlotNumber)
              );
              if (meta) {
                return {
                  ...meta,
                  [selectedInsight]: 0,
                  ...getOrderAndHeatKey(0, OrderByEnum.Asc, selectedInsight),
                };
              }
            }
            return null;
          })
          .filter(Boolean);
        const orderedSelectedMarkerSlots = sortByInsightOrder(
          selectedMarkerSlots as ICasinoReport[],
          selectedInsight,
          isTopTenSlot ? OrderByEnum.Desc : OrderByEnum.Asc
        );

        setMarkerSlots(orderedSelectedMarkerSlots);
      } else {
        const { top10, bottom10 } = getTopAndBottom(processedData, selectedInsight);
        setMarkerSlots(
          top10.map((slot, index) => ({
            ...slot,
            ...getOrderAndHeatKey(index, OrderByEnum.Desc, selectedInsight),
          }))
        );

        setMarkerColdestSlots(
          [...bottom10].reverse().map((slot, index) => ({
            ...slot,
            ...getOrderAndHeatKey(index, OrderByEnum.Asc, selectedInsight),
          }))
        );
      }
    }
  }, [
    slotData,
    slotDataIsLoading,
    selectedInsight,
    selectedSlots,
    showAllMarkers,
    previousSelectedSlotNumber,
    isTopTenSlot,
    slotMetaData?.DataSet,
    cloakedSlotNumbers,
  ]);

  useEffect(() => {
    if (geoJsonData && !getGeoJsonLoading) {
      if (
        geoJsonData.DataSet?.length > 0 &&
        geoJsonData.DataSet[0]?.geojson?.features?.length > 0
      ) {
        const clonedJson = JSON.parse(JSON.stringify(geoJsonData.DataSet[0].geojson));
        clonedJson.features.forEach((slot: GeoSlot, i: number) => {
          // LOTC floorPlanGeo often omits kp_floor_plan_location_pk — keep any
          // existing feature id, else synthesize so insightMapJoin can index.
          const pk = slot.properties?.kp_floor_plan_location_pk;
          slot.id =
            pk !== undefined && pk !== null
              ? pk
              : slot.id !== undefined && slot.id !== null
                ? slot.id
                : i + 1;
          const locationKey = `${slot.properties.area}${slot.properties.section}${slot.properties.location}`;
          slotLocationIdLookup.set(locationKey, slot.id);
        });
        // Insight rows join floorLocation ↔ properties.location_string (compact).
        // Index built in insightMapJoin; bump version so heat / pins re-run.
        setInsightIndexVersion(setInsightFeatureIndex(clonedJson.features));
        setSlotMapJsonWithIds(clonedJson);
      } else {
        setSlotMapJsonWithIds(null);
      }
    }
  }, [geoJsonData, getGeoJsonLoading]);

  const clearMapMarkers = (): void => {
    map?.fire('closeAllPopups');
    mapMarkers.forEach((marker) => marker._element.remove());
    setMapMarkers([]);
  };

  const clearStarMarkers = (): void => {
    starMarkersRef.current.forEach((marker) => marker.remove());
    starMarkersRef.current = [];
  };

  // Drop a gold star on EVERY EGM selected in the list (not just the top-pinned ones),
  // placed on the slot's tile so it's visible whether or not the slot also has a
  // numbered pin. Stars are their own markers so the numbered pins and map camera are
  // left untouched when the selection changes.
  const renderStarMarkers = (): void => {
    clearStarMarkers();
    if (!map || !slotData || !starredSlotNumbers || starredSlotNumbers.length === 0) return;

    const starredSet = new Set(starredSlotNumbers);
    const newStarMarkers: mapboxgl.Marker[] = [];

    slotData.forEach((slotDatum: any) => {
      const id = egmDetailId(slotDatum);
      if (id === null || !starredSet.has(id)) return;

      const targetGeoSlot: any = mapboxHelper.findTargetGeoSlot(
        slotMapJsonWithIds,
        slotLocationIdLookup,
        slotDatum
      );
      if (!targetGeoSlot) return;

      // Place the star at the tile centroid so it sits on the selected machine.
      const slotCentroid = centroid(targetGeoSlot);
      const lng = slotCentroid.geometry.coordinates[0];
      const lat = slotCentroid.geometry.coordinates[1];

      // Same NaN guard as createMarker — turf can emit invalid coords on bad geometry.
      if (
        typeof lng !== 'number' || typeof lat !== 'number' ||
        !isFinite(lng) || !isFinite(lat) ||
        isNaN(lng) || isNaN(lat)
      ) {
        return;
      }

      const marker = new mapboxgl.Marker({
        element: mapboxHelper.getGoldStarBadge(24),
        anchor: 'center',
      }).setLngLat([lng, lat]);
      marker.addTo(map);
      newStarMarkers.push(marker);
    });

    starMarkersRef.current = newStarMarkers;
  };

  const clearHeatValues = (): void => {
    slotMapJsonWithIds &&
      slotMapJsonWithIds.features.forEach((feature: IFeatures) => {
        if (feature.id !== undefined && feature.id !== null) {
          map.setFeatureState(
            {
              source: 'slotSource',
              id: feature.id,
            },
            {
              heat: 0,
            }
          );
        }
      });
  };

  const createMarker = (
    slotDatum: any,
    markerNumber: number,
    markerClass: string
  ): mapboxgl.Marker | undefined => {
    const targetGeoSlot: any = mapboxHelper.findTargetGeoSlot(
      slotMapJsonWithIds,
      slotLocationIdLookup,
      slotDatum
    );
    if (!targetGeoSlot || slotDatum[selectedInsight] <= -1) {
      return undefined;
    }

    const el = document.createElement('div');
    el.className = markerClass;

    if (markerNumber === 1) {
      el.setAttribute('home-page-tour-step', 'number-1-slot-map');
    }

    // if (markerClass !== 'mapboxgl-marker-transparent') {
    //   if (isProUser) {
    //     el.innerHTML = mapboxHelper.getColoredPin(markerNumber, '#AE9142');
    //   } else {
    //     el.innerHTML = mapboxHelper.getColoredPin(markerNumber, '#AE9142');
    //   }
    // }
    if (targetGeoSlot) {
      const slotCentroid = centroid(targetGeoSlot);
      const borderLocation: any = nearestPointOnLine(
        multiLineString(targetGeoSlot.geometry.coordinates),
        point(slotCentroid.geometry.coordinates)
      );

      const markerLocation = transformTranslate(
        slotCentroid,
        borderLocation.properties.dist / 1.5,
        0
      );

      // Validate coordinates before passing to Mapbox — turf functions can produce
      // NaN/Infinity coords when a slot's GeoJSON geometry is malformed or has empty
      // coordinate rings. setLngLat([NaN, NaN]) causes Mapbox to throw
      // "Invalid LngLat object: (NaN, NaN)" which propagates up through fitAllMarkers.
      const lng = markerLocation.geometry.coordinates[0];
      const lat = markerLocation.geometry.coordinates[1];
      if (
        typeof lng !== 'number' || typeof lat !== 'number' ||
        !isFinite(lng) || !isFinite(lat) ||
        isNaN(lng) || isNaN(lat)
      ) {
        return undefined;
      }

      const egmPin = challengeEGMPinConfig?.[String(slotDatum.slot_number)];
      const topPinLimit = Math.min(challengePinDetails?.showInsightTopPins ?? 0, 10);
      const showTopPinNumber =
        egmPin?.pinDisplayType &&
        egmPin?.pinDisplayCode &&
        topPinLimit > 0 &&
        markerNumber <= topPinLimit;
      const imageUrl =
        egmPin?.pinDisplayType && egmPin?.pinDisplayCode
          ? [S3_BASE, 'assets', String(egmPin.pinDisplayType), String(egmPin.pinDisplayCode)].join('/') + '.svg'
          : null;

      const pinElement = imageUrl
        ? showTopPinNumber
          ? mapboxHelper.createNumberedImagePin(
              imageUrl,
              markerNumber,
              undefined,
              undefined,
              challengePinDetails?.pinBackgroundColor ?? undefined,
              challengePinDetails?.pinNumberColor ?? undefined
            )
          : mapboxHelper.createImagePin(imageUrl)
        : mapboxHelper.createNumberedPin('#AE9142', markerNumber, 55);
      return new mapboxgl.Marker({
        element: pinElement,
        anchor: 'bottom',
      }).setLngLat([lng, lat]);
    }
  };

  const createMarkerWithStar = (
    slotCentroid: any,
    borderLocation: any,
    markerNumber: number,
    color: string = DETAIL_STAR_WHITE
  ): mapboxgl.Marker | undefined => {
    const el = document.createElement('div');
    el.className = 'mapboxgl-marker';

    const starDiv = document.createElement('div');
    starDiv.className = 'star';
    el.appendChild(starDiv);

    const markerLocation = transformTranslate(
      slotCentroid,
      borderLocation.properties.dist / 1.5,
      0
    );

    // Same NaN guard as createMarker — turf can produce invalid coords on bad geometry
    const lng = markerLocation.geometry.coordinates[0];
    const lat = markerLocation.geometry.coordinates[1];
    if (
      typeof lng !== 'number' || typeof lat !== 'number' ||
      !isFinite(lng) || !isFinite(lat) ||
      isNaN(lng) || isNaN(lat)
    ) {
      return undefined;
    }

    return new mapboxgl.Marker({
      element: mapboxHelper.getStarPin(1, color, 40),
      anchor: 'center',
    }).setLngLat([lng, lat]);
  };

  const handleIndividualSlot = (
    slotData: ICasinoReport[],
    individualSlotNumber: number,
    showMarker: boolean
  ) => {
    const individualSlot = mapboxHelper.findIndividualSlot(slotData, individualSlotNumber);
    if (individualSlot) {
      const individualSlotTargetGeoSlot =
        slotMapJsonWithIds &&
        slotMapJsonWithIds.features.find(
          (geoSlot: GeoSlot) =>
            geoSlot.id === mapboxHelper.geoSlotIdFor(slotLocationIdLookup, individualSlot)
        );
      if (individualSlotTargetGeoSlot !== undefined) {
        const slotCentroid = centroid(individualSlotTargetGeoSlot);
        // Only fly here; the focused star itself is drawn (and recolored on selection
        // change) by the dedicated focused-star effect below.
        const marker = createMarkerWithStar(
          centroid(individualSlotTargetGeoSlot),
          nearestPointOnLine(
            multiLineString(individualSlotTargetGeoSlot.geometry.coordinates),
            point(slotCentroid.geometry.coordinates)
          ),
          1
        );
        // createMarkerWithStar returns undefined when coordinates are invalid
        if (marker) {
          const markerLngLat = marker.getLngLat();
          const markerLongitude = markerLngLat.lng;
          const markerLatitude = markerLngLat.lat;
          individualSlotLngLat = new mapboxgl.LngLat(markerLongitude, markerLatitude);
          // Keep the user's zoom when already close enough; otherwise ease in so
          // the pin is readable in the top-25% band above the details drawer.
          const detailZoom = Math.max(map.getZoom(), 15);
          flyToMapCenter(individualSlotLngLat, detailZoom);
        }
      }
    }
  };

  // Fit the map view so all provided markers are visible.
  // mapBottomPadding is already applied to the Mapbox camera via easeTo — do NOT add it
  // here or the total padding exceeds the visible area and causes the map to go black.
  // Falls back to map center if any coordinates are invalid or missing.
  const fitAllMarkers = (markers: mapboxgl.Marker[]): void => {
    if (!map || markers.length === 0) return;

    const fallbackToCenter = () => {
      if (mapCenter?.geometry?.coordinates) {
        flyToMapCenter(
          { lng: mapCenter.geometry.coordinates[0], lat: mapCenter.geometry.coordinates[1] },
          17
        );
      }
    };

    try {
      // Safely read each marker's position — getLngLat() can throw if position was never set
      const coords: { lng: number; lat: number }[] = [];
      for (const m of markers) {
        try {
          const lngLat = m.getLngLat();
          if (
            lngLat &&
            typeof lngLat.lng === 'number' && typeof lngLat.lat === 'number' &&
            isFinite(lngLat.lng) && isFinite(lngLat.lat) &&
            !isNaN(lngLat.lng) && !isNaN(lngLat.lat)
          ) {
            coords.push({ lng: lngLat.lng, lat: lngLat.lat });
          }
        } catch {
          // skip this marker — no valid position
        }
      }

      if (coords.length === 0) { fallbackToCenter(); return; }
      if (coords.length === 1) { flyToMapCenter(coords[0], DETAIL_FOCUS_ZOOM); return; }

      const minLng = Math.min(...coords.map((c) => c.lng));
      const maxLng = Math.max(...coords.map((c) => c.lng));
      const minLat = Math.min(...coords.map((c) => c.lat));
      const maxLat = Math.max(...coords.map((c) => c.lat));

      if (!isFinite(minLng) || !isFinite(maxLng) || !isFinite(minLat) || !isFinite(maxLat)) {
        fallbackToCenter(); return;
      }

      map.fitBounds(
        new mapboxgl.LngLatBounds(
          new mapboxgl.LngLat(minLng, minLat),
          new mapboxgl.LngLat(maxLng, maxLat)
        ),
        { padding: { top: 60, bottom: 60, left: 40, right: 40 }, maxZoom: 20, animate: true }
      );
    } catch (e) {
      console.warn('[fitAllMarkers] failed, falling back to map center', e);
      fallbackToCenter();
    }
  };

  // After the first #1 pin focus on casino load, zoom in two MapControls steps
  // (~500ms), still centered on the pin. Clamped to map max zoom. Not used on
  // insight/timeframe pans (those keep the user's zoom via panToTopPinKeepingZoom).
  const scheduleFirstLoadExtraZoom = (
    pinLngLat: mapboxgl.LngLat,
    padding: { top: number; bottom: number; left: number; right: number }
  ): void => {
    if (!map) return;
    map.once('moveend', () => {
      if (!map) return;
      const focusZoom = map.getZoom();
      const nextZoom = resolveFirstLoadZoomAfterTopPin({
        focusZoom,
        maxZoom: map.getMaxZoom(),
      });
      if (nextZoom <= focusZoom) return;
      map.easeTo({
        center: [pinLngLat.lng, pinLngLat.lat],
        zoom: nextZoom,
        duration: FIRST_LOAD_EXTRA_ZOOM_DURATION_MS,
        essential: true,
        padding,
      });
    });
  };

  const focusOnFirstPinAfterDraw = (firstPinLngLat: mapboxgl.LngLat | null): boolean => {
    if (!map || !firstPinLngLat) return false;
    if (!isFinite(firstPinLngLat.lng) || !isFinite(firstPinLngLat.lat)) return false;

    try {
      // Fixed zoom level for pin 1 focus — ensures actual zoom-in on map load
      // (getMinZoom() + N was 13 which matched the initial zoom and had no effect)
      const pinFocusZoom = 15;

      // We bypass flyToMapCenter here intentionally — it always applies mapBottomPadding
      // which shifts the visual centre upward and breaks the desired vertical position.
      //
      // Mapbox places the center coordinate at the midpoint of the padded viewport:
      //   visual position from top = (mapHeight - bottomPadding) / 2
      // To land at 25% from the top:
      //   (H - B) / 2 = 0.25H  →  B = 0.5H
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const mapHeight = map.getContainer().clientHeight;
          const bottomPadding = Math.round(mapHeight * 0.5); // places pin at 25% from top
          const padding = { top: 0, bottom: bottomPadding, left: 0, right: 0 };

          map.flyTo({
            center: [firstPinLngLat.lng, firstPinLngLat.lat],
            zoom: pinFocusZoom,
            essential: true,
            animate: true,
            padding,
          });
          // One more + zoom step after focus settles (same as the + control).
          scheduleFirstLoadExtraZoom(firstPinLngLat, padding);
        });
      });

      return true;
    } catch {
      return false;
    }
  };

  // Insight/timeframe reload: keep the user's zoom, smoothly pan (~500ms) to #1.
  // Only fall back to the initial pin-focus zoom when the pin cannot be centered
  // at the current zoom (outside map max bounds).
  const panToTopPinKeepingZoom = (firstPinLngLat: mapboxgl.LngLat | null): boolean => {
    if (!map || !firstPinLngLat) return false;
    if (!isFinite(firstPinLngLat.lng) || !isFinite(firstPinLngLat.lat)) return false;

    try {
      const maxBounds = map.getMaxBounds();
      const canCenterAtCurrentZoom = !maxBounds || maxBounds.contains(firstPinLngLat);
      const zoom = resolveTopPinPanZoom({
        currentZoom: map.getZoom(),
        canCenterAtCurrentZoom,
        fallbackZoom: 15,
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const mapHeight = map.getContainer().clientHeight;
          const bottomPadding = Math.round(mapHeight * 0.5); // pin at ~25% from top

          map.easeTo({
            center: [firstPinLngLat.lng, firstPinLngLat.lat],
            zoom,
            duration: TOP_PIN_PAN_DURATION_MS,
            essential: true,
            padding: { top: 0, bottom: bottomPadding, left: 0, right: 0 },
          });
        });
      });

      return true;
    } catch {
      return false;
    }
  };

  const coldestSlots = (): void => {
    const markers: mapboxgl.Marker[] = [];
    let didFlyToIndividualSlot = false;
    let firstPinLngLat: mapboxgl.LngLat | null = null;

    markerColdestSlots.forEach((slotDatum: any, index: number) => {
      // Number the pin by its position in the ordered list (index + 1), NOT by a
      // success-only counter — so a slot with no map location leaves a gap instead of
      // shifting later pins, keeping pin numbers aligned with the EGM list row numbers.
      const marker = createMarker(
        slotDatum,
        index + 1,
        `mapboxgl-marker ${
          selectedInsight === 'jackpot_value' ||
          selectedInsight === 'jackpots' ||
          selectedInsight === 'pop'
            ? 'mapboxgl-cold-jkpt'
            : ''
        }`
      );
      if (marker) {
        setMapMarkers((prevMarkers) => [...prevMarkers, marker]);
        markers.push(marker);
        if (!firstPinLngLat) firstPinLngLat = marker.getLngLat();
        const markerLngLat = marker.getLngLat();
        const markerLongitude = markerLngLat.lng;
        const markerLatitude = markerLngLat.lat;
        // Detail focus always flies; multi-select auto-fly only before the initial
        // floor camera is locked (same policy as HottestSlots).
        const isDetailFocus = matchesEgmDetailId(slotDatum, individualSlotNumber);
        const isMultiSelectInitialFocus =
          !initialCameraFitDoneRef.current && selectedSlots.length > 1;
        if (isDetailFocus || isMultiSelectInitialFocus) {
          individualSlotLngLat = new mapboxgl.LngLat(markerLongitude, markerLatitude);
          if (individualSlotLngLat) {
            flyToMapCenter(individualSlotLngLat, selectedSlots.length > 1 ? 16.5 : map.getZoom());
            didFlyToIndividualSlot = true;
          }
        }
      }
    });

    // Draw pins first, then run focus.
    mapboxHelper.addMarkersToMap(map, markers);

    // When a pin is active the pin restore (in updateMapData) is the sole camera
    // authority. Skip the marker-focus camera move — focusOnFirstPinAfterDraw runs on a
    // deferred animation frame and would otherwise override the pinned center/zoom.
    // First paint: fit/focus with zoom. Later EGM reloads: pan to #1, keep zoom.
    if (
      shouldAutoFitMapCamera({
        initialFitDone: initialCameraFitDoneRef.current,
        pinSelected,
        didFlyToIndividualSlot,
        markerCount: markers.length,
      })
    ) {
      if (!focusOnFirstPinAfterDraw(firstPinLngLat)) {
        fitAllMarkers(markers);
      }
      initialCameraFitDoneRef.current = true;
    } else if (
      shouldPanToTopPinOnDataReload({
        initialFitDone: initialCameraFitDoneRef.current,
        pinSelected,
        didFlyToIndividualSlot,
        markerCount: markers.length,
      })
    ) {
      panToTopPinKeepingZoom(firstPinLngLat);
    }
  };

  const HottestSlots = (): void => {
    let markerNumber = 1;
    const markerValueMin = markerSlots.length > 1 ? -1 : -1;
    const markers: mapboxgl.Marker[] = [];
    let didFlyToIndividualSlot = false;
    let firstPinLngLat: mapboxgl.LngLat | null = null;

    markerSlots.forEach((slotDatum: any, index: number) => {
      // Number the pin by its position in the ordered list (index + 1), NOT by a
      // success-only counter — so a slot with no map location leaves a gap instead of
      // shifting later pins, keeping pin numbers aligned with the EGM list row numbers.
      const marker = createMarker(
        slotDatum,
        index + 1,
        markerSlots.length !== 1 ? 'mapboxgl-marker' : 'mapboxgl-marker-transparent'
      );

      if (marker) {
        markers.push(marker);
        if (!firstPinLngLat) firstPinLngLat = marker.getLngLat();
        setMapMarkers((prevMarkers) => [...prevMarkers, marker]);

        const markerLngLat = marker.getLngLat();
        const markerLongitude = markerLngLat.lng;
        const markerLatitude = markerLngLat.lat;

        // For a multi-slot filter (e.g. Filter by Game Title/Name) only fly to the
        // FIRST record's pin. This forEach matched every marker when selectedSlots > 1,
        // so flyToMapCenter ran once per marker and the camera ended on the LAST slot
        // instead of record 1. Gate on markerNumber === 1 to focus Position One.
        // showAllMarkers (Favorites/Playlist/Game filter) wants every pin visible, so
        // skip the fly-to-first-pin focus and let fitAllMarkers frame them all below.
        // In challenge Explore the camera is handled after the loop (centerOnPin on
        // pin 1); skip the in-loop fly so it doesn't apply the off-centre latitude
        // nudge and tight 16.5 zoom.
        // After the initial floor fit, skip multi-select auto-fly so timeframe/insight
        // reloads keep the user's camera; detail focus (individualSlotNumber) still flies.
        const isDetailFocus = matchesEgmDetailId(slotDatum, individualSlotNumber);
        const isMultiSelectInitialFocus =
          !initialCameraFitDoneRef.current &&
          !showAllMarkers &&
          !challengeEGMPinConfig &&
          selectedSlots.length > 1 &&
          markerNumber === 1;
        if (isDetailFocus || isMultiSelectInitialFocus) {
          individualSlotLngLat = new mapboxgl.LngLat(markerLongitude, markerLatitude);
          flyToMapCenter(individualSlotLngLat, selectedSlots.length > 1 ? 16.5 : map.getZoom());
          didFlyToIndividualSlot = true;
        }

        markerNumber++;
      }
    });

    // Draw pins first, then run focus.
    mapboxHelper.addMarkersToMap(map, markers);

    // When a pin is active the pin restore (in updateMapData) is the sole camera
    // authority. Skip the marker-focus camera move — focusOnFirstPinAfterDraw runs on a
    // deferred animation frame and would otherwise override the pinned center/zoom.
    // First paint: fit/focus with zoom. Later EGM reloads: pan to #1, keep zoom.
    if (
      shouldAutoFitMapCamera({
        initialFitDone: initialCameraFitDoneRef.current,
        pinSelected,
        didFlyToIndividualSlot,
        markerCount: markers.length,
      })
    ) {
      if (challengeEGMPinConfig) {
        // Challenge Explore: center pin 1 in the visible area above the drawer at a
        // moderate zoom, so the numbered pin 1 is the focus while nearby machines stay
        // visible for context. Not fitAllMarkers (frames the whole set) and not the
        // off-centre flyToMapCenter. Then +1 zoom step like the standard first load.
        centerOnPin(firstPinLngLat, CHALLENGE_PIN_FOCUS_ZOOM);
        if (firstPinLngLat) {
          scheduleFirstLoadExtraZoom(firstPinLngLat, {
            top: 0,
            bottom: mapBottomPadding,
            left: 0,
            right: 0,
          });
        }
      } else if (!focusOnFirstPinAfterDraw(firstPinLngLat)) {
        fitAllMarkers(markers);
      }
      initialCameraFitDoneRef.current = true;
    } else if (
      shouldPanToTopPinOnDataReload({
        initialFitDone: initialCameraFitDoneRef.current,
        pinSelected,
        didFlyToIndividualSlot,
        markerCount: markers.length,
      })
    ) {
      panToTopPinKeepingZoom(firstPinLngLat);
    }
  };

  useEffect(() => {
    if (map) {
      mapboxHelper.setPreferenceHeatMapColor(heatFilter, map, isJackpotInsight(selectedInsight));
    }
  }, [heatFilter]);

  // Timeframe/insight refresh: clear heat + pins while the floor plan stays up.
  useEffect(() => {
    if (!map || !hideEgmOverlays) return;
    clearMapMarkers();
    if (map.getSource('slotSource')) clearHeatValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideEgmOverlays, map]);

  const updateMapData = (): void => {
    if (!map || !heatBins || !map.getSource('slotSource')) return;
    clearMapMarkers();
    clearHeatValues();
    insertGameNames();

    const totalSlots = {
      total: slotData,
      beanData: heatBins,
    };

    if (onFloorStats) {
      onFloorStats(totalSlots);
    }

    Object.keys(heatBins).forEach((key: any) => {
      heatBins[key].forEach((slot: Slot) => {
        const slotId = mapboxHelper.geoSlotIdFor(slotLocationIdLookup, slot);
        if (slotId !== undefined) {
          map.setFeatureState(
            {
              source: 'slotSource',
              id: slotId,
            },
            {
              heat: parseInt(key),
            }
          );
        }
      });
    });
    mapboxHelper.setPreferenceHeatMapColor(heatFilter, map, isJackpotInsight(selectedInsight));
    // Tracks whether the individual-EGM branch ran, so the empty-map recenter below
    // can skip when that branch owns the camera. Set inside the narrowing `if` so
    // TypeScript still narrows individualSlotNumber to a number for handleIndividualSlot.
    let isIndividualFocus = false;
    if (!shouldHideSlotMark && individualSlotNumber && !previousSelectedSlotNumber && slotData) {
      isIndividualFocus = true;
      handleIndividualSlot(slotData, individualSlotNumber, true);
    } else if (showAllMarkers) {
      // All saved EGMs are held in markerSlots; render them regardless of sort direction.
      HottestSlots();
    } else {
      if (selectedSlots.length === 0) {
        if (isTopTenSlot) {
          HottestSlots();
        } else {
          coldestSlots();
        }
      } else {
        HottestSlots();
      }
    }

    // No data / no pins to place: HottestSlots/coldestSlots draw nothing and
    // fitAllMarkers bails on its empty guard, so the camera would stay wherever it
    // last sat (e.g. zoomed in on a prior filter). Recenter on the floor's default
    // view so the empty map is framed in the visible area above the drawer instead
    // of drifting. Skipped when a pin is active, an individual EGM is focused, or
    // the camera was already established (timeframe/insight EGM reload).
    if (
      shouldRecenterEmptyMap({
        initialFitDone: initialCameraFitDoneRef.current,
        pinSelected,
        isIndividualFocus,
        hasHotMarkers: markerSlots.length > 0,
        hasColdMarkers: markerColdestSlots.length > 0,
      })
    ) {
      recenterMap();
      initialCameraFitDoneRef.current = true;
    }
    if (pinSelected) {
      pinLatitudeLongitude &&
        pinZoom &&
        flyToMapCenter(
          new mapboxgl.LngLat(pinLatitudeLongitude?.longitude, pinLatitudeLongitude?.latitude),
          pinZoom
        );
    }

    // Detect a double tap by counting two 'click's on a pin within the window. We do
    // NOT use Mapbox's 'dblclick' event: it is mouse-only and never fires on touch, so
    // on a phone the second tap would never register. 'click' fires on every tap on both
    // touch and mouse, so counting them works everywhere. The native double-tap/click-to-zoom
    // is disabled at map creation, which frees the gesture (and on touch keeps the two
    // taps from being swallowed by the zoom handler). A lone tap (window elapses) fires
    // onClickSlot; a second tap within the window fires onDoubleClickSlot. The parent
    // decides what each does (see MapStats).
    if (slotClickHandlerRef.current) {
      map.off('click', 'slotLayer', slotClickHandlerRef.current);
    }
    slotClickHandlerRef.current = (evt: any) => {
      if (evt?.features?.[0]?.properties && slotData) {
        const playDataSlot = mapboxHelper.findPlayDataSlot(evt, slotData);
        if (playDataSlot) {
          const detailId = egmDetailId(playDataSlot);
          if (detailId === null) return;
          individualSlotNumber = detailId;
          if (slotClickTimer.current) {
            clearTimeout(slotClickTimer.current);
            slotClickTimer.current = null;
            if (onDoubleClickSlotRef.current) {
              onDoubleClickSlotRef.current(playDataSlot);
            } else {
              onClickSlotRef.current(playDataSlot);
            }
          } else {
            slotClickTimer.current = setTimeout(() => {
              slotClickTimer.current = null;
              onClickSlotRef.current(playDataSlot);
            }, 300);
          }
        }
      }
    };
    map.on('click', 'slotLayer', slotClickHandlerRef.current);

    // Stop the default double-click-to-zoom when a pin is double-tapped so the
    // gesture cleanly opens the detail view instead of zooming the floor.
    map.on('dblclick', 'slotLayer', (evt: any) => {
      evt?.preventDefault?.();
    });

    map.on('mousemove', 'slotLayer', (evt: any) => {
      if (evt?.features?.[0]?.properties) {
        map.getCanvas().style.cursor = 'pointer';
      }
    });

    map.on('mouseleave', 'slotLayer', () => {
      map.getCanvas().style.cursor = '';
    });

    map.on('closeAllPopups', () => {
      tooltip.remove();
    });

    if (bearing) {
      floormapCommand.SetBearingAction(bearing);
    }

    map.on('dragend', () => {
      floormapCommand.setMapValues({
        bearing: map.getBearing(),
        latitudeLongitude: map.getCenter(),
        zoom: map.getZoom(),
      });
    });

    map.on('zoom', () => {
      if (!individualSlotNumber) {
        floormapCommand.setMapValues({
          bearing: map.getBearing(),
          latitudeLongitude: map.getCenter(),
          zoom: map.getZoom(),
        });
      }
    });

    map.on('rotate', () => {
      floormapCommand.setMapValues({
        bearing: map.getBearing(),
        latitudeLongitude: map.getCenter(),
        zoom: map.getZoom(),
      });
    });
  };

  useEffect(() => {
    if (map) {
      for (let i = mapMarkers.length - 1; i >= 0; i--) {
        mapMarkers[i].addTo(map);
      }
    }
  }, [mapMarkers]);

  useEffect(() => {
    if (map?.getSource('wallSource')) {
      map.getSource('wallSource').setData({
        type: 'FeatureCollection',
        features: wallMapJson?.features || [],
      });
    }

    // Update 'labelSource' features
    if (map?.getSource('labelSource')) {
      map.getSource('labelSource').setData({
        type: 'FeatureCollection',
        features: labelMapJson?.features || [],
      });
    }

    // Update 'bankIdSource' features
    if (map?.getSource('bankIdSource')) {
      map.getSource('bankIdSource').setData({
        type: 'FeatureCollection',
        features: bankIdMapJson?.features || [],
      });
    }
  }, [map, wallMapJson, labelMapJson, bankIdMapJson]);

  // Bank tap-to-filter: only when this casino's own *-bank-ids.json loaded.
  // Missing/unmapped → no handler (labels/walls/bank layers may still render).
  useEffect(() => {
    if (!map) return;

    if (bankLabelClickHandlerRef.current) {
      map.off('click', bankLabelClickHandlerRef.current);
      bankLabelClickHandlerRef.current = null;
    }

    bankLabelClickHandlerRef.current = (evt: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (!onClickBankLabelRef.current) return;
      const point = evt?.point;
      if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') return;

      const box: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [point.x - 16, point.y - 16],
        [point.x + 16, point.y + 16],
      ];

      const egmLayers = ['slotLayer'].filter((id) => Boolean(map.getLayer(id)));
      if (egmLayers.length > 0) {
        const egmHits = map.queryRenderedFeatures(box, { layers: egmLayers });
        if (egmHits && egmHits.length > 0) return;
      }

      const labelLayers = existingBankLabelLayerIds(map);
      if (labelLayers.length === 0) return;

      const labelHits = map.queryRenderedFeatures(box, { layers: labelLayers });
      if (!labelHits || labelHits.length === 0) return;

      let nearest = labelHits[0];
      let nearestDist = Number.POSITIVE_INFINITY;
      for (const feat of labelHits) {
        const geom = feat.geometry as { type?: string; coordinates?: number[] } | undefined;
        if (geom?.type === 'Point' && Array.isArray(geom.coordinates)) {
          const projected = map.project(geom.coordinates as [number, number]);
          const dx = projected.x - point.x;
          const dy = projected.y - point.y;
          const d = dx * dx + dy * dy;
          if (d < nearestDist) {
            nearestDist = d;
            nearest = feat;
          }
        }
      }

      const label = bankLabelTextFromProperties(
        nearest?.properties as Record<string, unknown>
      );
      if (!label) return;

      const bankKey = label.toUpperCase().replace(/\s+/g, '').replace(/^BANK/, '');
      const bankSlots = (casinoSearchData?.data ?? []).filter((slot: any) => {
        const sec = String((slot as any).section ?? '').trim().toUpperCase();
        const bnk = String((slot as any).bank ?? '').trim().toUpperCase();
        return `${sec}${bnk}` === bankKey;
      });

      let lngLat: { lng: number; lat: number } | null = null;
      const g = nearest.geometry as { type?: string; coordinates?: number[] } | undefined;
      if (g?.type === 'Point' && Array.isArray(g.coordinates) && g.coordinates.length >= 2) {
        lngLat = { lng: Number(g.coordinates[0]), lat: Number(g.coordinates[1]) };
      } else if (evt.lngLat) {
        lngLat = { lng: evt.lngLat.lng, lat: evt.lngLat.lat };
      }

      onClickBankLabelRef.current({ label, lngLat });
    };
    map.on('click', bankLabelClickHandlerRef.current);

    return () => {
      if (bankLabelClickHandlerRef.current) {
        map.off('click', bankLabelClickHandlerRef.current);
        bankLabelClickHandlerRef.current = null;
      }
    };
  }, [map]);

  const flyToMapCenter = (locationLngLat: any, zoomValue: any, pinPositionFromTop?: number): void => {
    // Detail map: no latitude nudge — pin must land dead-centre in the clear
    // band between the header/pills overlay and the 75% EGM Details drawer.
    let offsetLat =
      mapId === MapTypeEnum.individualReportMap ? 0 : 0.0007;
    if (pinSelected) {
      offsetLat = 0;
    }

    if (locationLngLat?.lng) {
      const updatedCenter = {
        lng: locationLngLat?.lng,
        lat: locationLngLat?.lat - offsetLat,
      };

      if (map) {
        const mapContainer = map.getContainer();
        const mapHeight = mapContainer.clientHeight;
        // If a pinPositionFromTop is provided, add extra shift on top of the drawer padding.
        // Otherwise just use mapBottomPadding so the floor is centred in the visible area.
        const extraPadding = pinPositionFromTop !== undefined
          ? Math.max(0, Math.round(2 * (0.5 - pinPositionFromTop) * mapHeight))
          : 0;

        let topPadding = 0;
        let totalBottomPadding = Math.max(mapBottomPadding, extraPadding);

        // EGM Details: top = measured header+pills overlay over the map; bottom =
        // 75% drawer (or live mapBottomPadding once it catches up). Centres the
        // pin below the pills, not under them. List-map path unchanged (top=0).
        if (mapId === MapTypeEnum.individualReportMap) {
          const overlayTop = measureDetailFocusTopPadding(mapContainer);
          const detailPad = detailFocusCameraPadding(mapHeight, overlayTop);
          topPadding = detailPad.top;
          totalBottomPadding = Math.max(mapBottomPadding, detailPad.bottom, extraPadding);
        }

        map.flyTo({
          center: updatedCenter,
          zoom: zoomValue,
          essential: true,
          animate: !pinSelected,
          padding: { top: topPadding, bottom: totalBottomPadding, left: 0, right: 0 },
        });
      }
    }
  };

  // Center a single pin in the *visible* map area (the part above the drawer) at a
  // fixed zoom. Unlike flyToMapCenter this applies no latitude nudge, so the pin lands
  // dead-centre rather than pushed off to one side. Used to focus challenge Explore on
  // pin 1.
  const centerOnPin = (lngLat: mapboxgl.LngLat | null, zoom: number): void => {
    if (!map || !lngLat) return;
    if (!isFinite(lngLat.lng) || !isFinite(lngLat.lat)) return;
    map.flyTo({
      center: [lngLat.lng, lngLat.lat],
      zoom,
      essential: true,
      padding: { top: 0, bottom: mapBottomPadding, left: 0, right: 0 },
    });
  };

  // Recenter the map to its default center and zoom (the same view it opens with),
  // keeping the floor framed in the visible area above the drawer.
  const recenterMap = (): void => {
    if (!map) return;
    const defaultZoom = locationLatLon ? 16 : 13;
    const center = locationLatLon
      ? { lng: locationLatLon.lat, lat: locationLatLon.lon - 0.00175 }
      : mapCenter?.geometry?.coordinates
        ? { lng: mapCenter.geometry.coordinates[0], lat: mapCenter.geometry.coordinates[1] }
        : null;
    if (!center) return;
    map.flyTo({
      center,
      zoom: defaultZoom,
      essential: true,
      padding: { top: 0, bottom: mapBottomPadding, left: 0, right: 0 },
    });
  };

  // Pan/zoom the map so a single EGM (just selected in the list) is centred and its
  // gold star is visible. Reuses the same geo-slot lookup as the star overlay.
  const flyToSlotNumber = (slotNumber: number): void => {
    if (!map || !slotData) return;
    // Insight: match mnum (egmDetailId); legacy: slot_number.
    const slotDatum = slotData.find((s: any) => matchesEgmDetailId(s, slotNumber));
    if (!slotDatum) return;
    const targetGeoSlot: any = mapboxHelper.findTargetGeoSlot(
      slotMapJsonWithIds,
      slotLocationIdLookup,
      slotDatum
    );
    if (!targetGeoSlot) return;

    const slotCentroid = centroid(targetGeoSlot);
    const lng = slotCentroid.geometry.coordinates[0];
    const lat = slotCentroid.geometry.coordinates[1];
    // Same NaN guard as the marker helpers — turf can emit invalid coords on bad geometry.
    if (
      typeof lng !== 'number' || typeof lat !== 'number' ||
      !isFinite(lng) || !isFinite(lat)
    ) {
      return;
    }

    // Preserve the user's current zoom — selecting an EGM should only recentre,
    // never change the zoom level.
    // Centre the EGM in the *visible* map area (the part not covered by the drawer).
    // We fly directly rather than via flyToMapCenter so we can (a) reserve the
    // drawer-covered height as bottom padding — Mapbox then centres the slot in the
    // uncovered region instead of the full container — and (b) skip flyToMapCenter's
    // fixed latitude nudge, which pushed the star off-centre.
    map.flyTo({
      center: { lng, lat },
      zoom: map.getZoom(),
      essential: true,
      padding: { top: 0, bottom: mapBottomPadding, left: 0, right: 0 },
    });
  };

  const setGameNameProperties = (targetGeoSlot: GeoSlot, slot: any): void => {
    if (slot.slot_number !== 0) {
      // Insight slots: return arrives as a percent, so hand the formatter the
      // fraction; label = "{displayId} - Bank {section}{bank}" with raw names
      // (displayId = machines.slotNumber, else mnum — never floorLocation).
      const isInsightSlot = isInsightCardRow(slot);
      const legacy = InsertGameNameToSlot(
        selectedInsight,
        isInsightSlot ? withReturnFraction(slot) : slot,
        slotData
      );
      const { slotNumber, gameTitle, gameTheme } = isInsightSlot
        ? insightMapLabels(slot)
        : legacy;
      const { insightText } = legacy;
      targetGeoSlot.properties.game_slot_number = slotNumber;
      targetGeoSlot.properties.game_title = gameTitle;
      targetGeoSlot.properties.game_theme = gameTheme;
      targetGeoSlot.properties.game_insight = insightText;
      targetGeoSlot.properties.game_name = gameTitle;
    } else {
      const gameTitle = getCleanGameName(slot.game_name);
      targetGeoSlot.properties.game_name = `${titleCase(gameTitle)}`;
      delete targetGeoSlot.properties.game_slot_number;
      delete targetGeoSlot.properties.game_title;
      delete targetGeoSlot.properties.game_theme;
      delete targetGeoSlot.properties.game_insight;
    }
  };

  const insertGameNameIntoGeoJson = (slot: any): void => {
    const targetGeoSlot = mapboxHelper.findTargetGeoSlot(
      slotMapJsonWithIds,
      slotLocationIdLookup,
      slot
    );
    if (targetGeoSlot) {
      setGameNameProperties(targetGeoSlot, slot);
    }
  };

  const updateSlotSourceData = (): void => {
    if (map?.getSource('slotSource') !== undefined && slotMapJsonWithIds) {
      map.getSource('slotSource').setData({
        type: 'FeatureCollection',
        features: slotMapJsonWithIds.features,
      });
    }
  };

  const insertGameNames = (): void => {
    if (slotMetaData?.DataSet !== undefined) {
      slotMetaData.DataSet.forEach((slot: Slot) => {
        insertGameNameIntoGeoJson(slot);
      });
    }

    if (heatBins !== undefined) {
      Object.keys(heatBins).forEach((key) => {
        heatBins[key].forEach((slot: Slot) => {
          insertGameNameIntoGeoJson(slot);
        });
      });

      updateSlotSourceData();
    }
  };

  // Cold start: insight rows render first (fallback mnum); when GetSearchData
  // lands, refresh popup/label display ids without clearHeatValues flash.
  useEffect(() => {
    if (!map || !heatBins || !slotMapJsonWithIds) return;
    if (!casinoSearchData?.data?.length) return;
    if (casinoSearchData.casinoId != null && casinoId != null && casinoSearchData.casinoId !== casinoId) {
      return;
    }
    insertGameNames();
    // insertGameNames closes over latest heatBins / geojson / catalog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casinoSearchData, casinoId, map, heatBins, slotMapJsonWithIds]);

  const onResize = useCallback(() => {
    const handleLoad = () => map?.resize();
    const handleIdle = () => {
      setIsDataLoaded(true);
      map?.resize();
    };
    const handleRender = () => map?.resize();
    const handleStyleLoad = () => map?.resize();

    map?.once('load', handleLoad);
    map?.on('idle', handleIdle);
    map?.on('render', handleRender);
    map?.on('style.load', handleStyleLoad);
    map?.on('load', handleLoad);

    const timeoutId = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      map?.off('idle', handleIdle);
      map?.off('render', handleRender);
      map?.off('style.load', handleStyleLoad);
      map?.off('load', handleLoad);
    };
  }, [map]);

  useEffect(() => {
    return onResize();
  }, [onResize]);

  useIonViewWillEnter(() => {
    onResize();
  });

  useIonViewDidEnter(() => {
    updateMapData();
  });

  useEffect(() => {
    const allDataLoaded = wallMapJson && bankIdMapJson && labelMapJson && slotMapJsonWithIds;
    if (allDataLoaded) {
      getInitializeMap();
    }
  }, [wallMapJson, bankIdMapJson, labelMapJson, slotMapJsonWithIds]);

  const getInitializeMap = useCallback(() => {
    const initializeMap = (): void => {
      const mapCenterLng = locationLatLon
        ? locationLatLon.lat
        : mapCenter?.geometry.coordinates[0] ?? 0; // Default to 0 if undefined
      const mapCenterLat = locationLatLon
        ? locationLatLon.lon - 0.00175
        : mapCenter?.geometry.coordinates[1] ?? 0; // Default to 0 if undefined

      const mapbox = new mapboxgl.Map({
        container: mapContainer.current,
        style: config.mapbox.styleUrl, // style URL
        center: [mapCenterLng, mapCenterLat], // starting position [lng, lat]
        zoom: locationLatLon ? 16 : 13, // starting zoom
        minZoom: 10,
        maxZoom: 24,
        maxBounds: maxBounds,
      });
      config.mapbox.hide3dview && mapbox?.touchPitch.disable();
      // Double-tapping a pin opens its EGM detail; reserve the gesture for that instead
      // of zooming the floor. (Pinch / scroll / zoom controls still zoom.)
      mapbox?.doubleClickZoom.disable();
      mapbox?.resize();
      mapbox.on('load', () => {
        // Make the Mapbox style's background layer transparent so the
        // .mapstats gradient shows through outside the floor outline.
        const bgLayer = mapbox
          .getStyle()
          ?.layers?.find((l: { type: string }) => l.type === 'background');
        if (bgLayer) {
          mapbox.setPaintProperty(bgLayer.id, 'background-opacity', 0);
        }
        // Add slots geoJSON
        mapbox.resize();
        // Apply initial drawer padding so the floor is centred in the visible area
        mapbox.easeTo({
          padding: { top: 0, bottom: mapBottomPadding, left: 0, right: 0 },
          duration: 0,
        });

        // Display slots on a layer
        // addsource label,bankid,walls
        addMapSources(mapbox, {
          slotMapJsonWithIds: slotMapJsonWithIds ?? null,
          wallMapJson: wallMapJson ?? null,
          labelMapJson: labelMapJson ?? null,
          bankIdMapJson: bankIdMapJson ?? null,
        });

        // Draw thicker borders between slots
        setupHeatLayer(mapbox); // heatbean color for slot
        setupBorderLayer(mapbox); // border line between slot

        setupBankIdLayers(mapbox, casinoId); // add bankid against casino
        setupAddWallLayer(mapbox); // add walllayer for casino
        setupSlotTextLayer(mapbox); // add slot EGM name in mapbox
        setupCasinoLayers(mapbox, casinoId); // add font,icon size in mapbox

        setMap(mapbox);
        setDisplayLegend(true);
        setIsMapInitialized(true);
      });
    };
    // slotMapJsonWithIds is:
    //   undefined  → still waiting (geojson not resolved yet)
    //   null       → geojson resolved but has no slot polygons (e.g. new casino with null DB geojson)
    //   GeoJson    → slot polygons available
    const slotsResolved = slotMapJsonWithIds !== undefined;
    // When there are no slot polygons, wait until at least wallMapJson is ready
    // so the map has something to show before it initialises.
    const readyToInit =
      slotsResolved &&
      (slotMapJsonWithIds !== null || wallMapJson !== undefined) &&
      heatBins !== undefined;

    if (readyToInit) {
      if (map) {
        setMap(map);
      }
      if (map === undefined) {
        initializeMap();
      }

      if (map) {
        if (isShowData) {
          updateMapData();
        }
      }
    } else {
      if (!geoJsonData && !getGeoJsonLoading) {
        if (map) {
          setMap(map);
          setIsDataLoaded(true);
        }
        if (map === undefined) {
          initializeMap();
        }
      }
    }
  }, [wallMapJson, labelMapJson, bankIdMapJson, heatBins, slotMapJsonWithIds]);

  useEffect(() => {
    if (isShowData) {
      updateMapData();
    }
  }, [map, slotMetaData?.DataSet, mapId, heatBins]);

  // Markers-only effect: when sort direction toggles, just swap which pin set is shown
  // without re-running updateMapData (which calls clearHeatValues and causes a blank flash).
  useEffect(() => {
    if (!isShowData || !map || !heatBins || !map.getSource('slotSource')) return;
    clearMapMarkers();
    if (!shouldHideSlotMark && individualSlotNumber && !previousSelectedSlotNumber && slotData) {
      handleIndividualSlot(slotData, individualSlotNumber, true);
    } else if (showAllMarkers) {
      HottestSlots();
    } else if (selectedSlots.length === 0) {
      isTopTenSlot ? HottestSlots() : coldestSlots();
    } else {
      HottestSlots();
    }
  }, [isTopTenSlot]);

  // Redraw the gold stars whenever the list selection or the slot/geo data changes.
  useEffect(() => {
    if (!isShowData || !map) return;
    renderStarMarkers();
  }, [
    map,
    isShowData,
    starredSlotNumbers.join(','),
    slotData,
    slotMapJsonWithIds,
  ]);

  // Draw the focused EGM's star on the detail (individual) map, colored by selection:
  // gold when that EGM is selected in the list, white when it is not. Kept in its own
  // marker ref so a selection toggle recolors the star in place — no camera re-fly and
  // no pin redraw. Keyed on starredSlotNumbers so tapping to select flips it live.
  useEffect(() => {
    if (!map) return;
    focusedStarMarkerRef.current?.remove();
    focusedStarMarkerRef.current = null;
    if (
      shouldHideSlotMark ||
      !individualSlotNumber ||
      previousSelectedSlotNumber ||
      !slotData ||
      !slotMapJsonWithIds
    ) {
      return;
    }
    const individualSlot = mapboxHelper.findIndividualSlot(slotData, individualSlotNumber);
    if (!individualSlot) return;
    const targetGeoSlot: any = slotMapJsonWithIds.features.find(
      (geoSlot: GeoSlot) =>
        geoSlot.id === mapboxHelper.geoSlotIdFor(slotLocationIdLookup, individualSlot)
    );
    if (!targetGeoSlot) return;
    const slotCentroid = centroid(targetGeoSlot);
    const borderLocation = nearestPointOnLine(
      multiLineString(targetGeoSlot.geometry.coordinates),
      point(slotCentroid.geometry.coordinates)
    );
    const isSelected = starredSlotNumbers.includes(individualSlotNumber);
    const marker = createMarkerWithStar(
      slotCentroid,
      borderLocation,
      1,
      isSelected ? DETAIL_STAR_GOLD : DETAIL_STAR_WHITE
    );
    if (marker) {
      marker.addTo(map);
      focusedStarMarkerRef.current = marker;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    individualSlotNumber,
    shouldHideSlotMark,
    previousSelectedSlotNumber,
    slotData,
    slotMapJsonWithIds,
    starredSlotNumbers.join(','),
  ]);

  // Remove any star markers when the map unmounts.
  useEffect(
    () => () => {
      clearStarMarkers();
      focusedStarMarkerRef.current?.remove();
      focusedStarMarkerRef.current = null;
    },
    []
  );

  // Fly to an EGM the moment it's selected in the list, so its gold star comes into
  // view. nonce changes per selection so re-selecting the same EGM re-triggers it.
  useEffect(() => {
    if (!isShowData || !map || !mapFocusSlot) return;
    flyToSlotNumber(mapFocusSlot.slotNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapFocusSlot?.nonce]);

  // Notify the parent when the user moves the map by gesture so it can reset its
  // name-tap focus. Programmatic camera moves (flyTo) carry no originalEvent, so
  // re-centring an EGM via mapFocusSlot does not count as a user move.
  useEffect(() => {
    if (!map || !onMapMove) return;
    const handleUserMove = (e: any) => {
      if (e?.originalEvent) onMapMove();
    };
    map.on('movestart', handleUserMove);
    return () => {
      map.off('movestart', handleUserMove);
    };
  }, [map, onMapMove]);

  // Track whether the map is moving so the recenter icon can hide during motion
  // and fade back in once the camera settles. Covers both user gestures and
  // programmatic flyTo/easeTo (e.g. tapping recenter itself).
  useEffect(() => {
    if (!map) return;
    const handleMoveStart = () => setMapMoving(true);
    const handleMoveEnd = () => setMapMoving(false);
    map.on('movestart', handleMoveStart);
    map.on('moveend', handleMoveEnd);
    return () => {
      map.off('movestart', handleMoveStart);
      map.off('moveend', handleMoveEnd);
    };
  }, [map]);

  useEffect(() => {
    if (map) {
      mapboxHelper.setPreferenceHeatMapColor(heatFilter, map, isJackpotInsight(selectedInsight));
    }
  }, [heatFilter.join('')]);

  const displayMapError = (): void => {
    setIsDataLoaded(true);
    setLoadError(true);
    void presentMapError({
      message: 'Grumble... we ran into an issue loading the map data. Sorry!',
      duration: 3000,
    });
  };


  // When the drawer position changes, expand maxBounds southward so the user can
  // pan the casino floor into the visible area above the drawer.
  // NOTE: We intentionally do NOT call map.easeTo() here — doing so would pan
  // the map camera every time the drawer snaps, which is unwanted. mapBottomPadding
  // is still consumed by flyToMapCenter so slot centering remains accurate.
  useEffect(() => {
    if (!map) return;

    if (wallMapJson) {
      const mapBounds = bbox(wallMapJson);
      const zoom = map.getZoom();
      const pixelsPerDegreeLat = (256 * Math.pow(2, zoom)) / 360;
      const extraDegrees = mapBottomPadding / pixelsPerDegreeLat;
      const MARGIN_LNG = 0.01;
      const MARGIN_BASE = 0.03;
      const newMaxBounds: mapboxgl.LngLatBoundsLike = [
        [mapBounds[0] - MARGIN_LNG, mapBounds[1] - MARGIN_BASE - extraDegrees],
        [mapBounds[2] + MARGIN_LNG, mapBounds[3] + MARGIN_BASE + extraDegrees],
      ];
      map.setMaxBounds(newMaxBounds);
    }

    map.resize();
  }, [mapBottomPadding, map, wallMapJson]);

  return (
    <div className="home-container">
      <div className="floor-map-container">
        {/* Mapbox owns this node — keep chrome overlays as siblings so absolute
            positioning is always relative to the map area (.floor-map-container),
            never the casino header / viewport. */}
        <div
          id={mapId}
          className={`floor-map ${isMapInitialized ? '' : 'floor-map--loading'}`}
          ref={(el) => (mapContainer.current = el)}
        />
        <div
          className={`floor-map-overlays${
            hideEgmOverlays ? ' floor-map-overlays--hidden' : ' floor-map-overlays--visible'
          }`}
          aria-hidden={hideEgmOverlays}
        >
          {displayLegend && (
            <HeatLegend
              onClick={(data: any) => {
                onClickHeatFilter(data);
              }}
              onSelectHeatLevels={onSelectHeatLevels}
              coldFocus={coldFocus}
              selectedInsight={selectedInsight}
              direction={DirectionType.vertical}
              heatFilter={[...heatFilter]}
              map={map}
            />
          )}
          {map && <MapControls map={map} pinSelected={pinSelected} mapId={mapId} />}
        </div>
        {/* Recenter-to-default button: sits just above the drawer top on the right.
            Hidden once the drawer is fully open (panelTop ≈ 0, i.e. no visible map). */}
        {map && mapBottomPadding > 0 && window.innerHeight - mapBottomPadding > 100 && (
          <button
            type="button"
            className={`map-recenter-button${
              mapMoving || drawerDragging || hideEgmOverlays ? ' is-hidden' : ''
            }`}
            style={{ bottom: `${mapBottomPadding + 12}px` }}
            onClick={recenterMap}
            aria-label="Recenter map"
          >
            <RecenterIcon />
          </button>
        )}
        {loadError && (
          <div className="loader-container">
            <div style={{ height: '100%', textAlign: 'center' }}>
              <span style={{ paddingTop: '20%', display: 'block' }}>Error loading map.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

FloorMap.defaultProps = {
  isShowData: true,
};
export default FloorMap;
